// MobileLogSheetLogList — last-N log entries with horizontal swipe-to-
// delete (per Apple HIG swipe-to-delete). Per `loop-goals/W34.2-COMPREHENSIVE-PROMPT.md`
// outcome W34.2.0, swipe-to-delete is enabled only for rows authored by
// the current user (matches the backend DELETE permission rule per
// API_REFERENCE §5.3.3).
//
// Vertical scroll is preserved: we ignore pointer moves whose |dy| >
// |dx| so the inner list doesn't fight the page scroll. The 60pt swipe
// threshold mirrors iOS native list delete (Apple HIG).

import { useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import type { ProgressLogOut } from "@/lib/api/wishBlocks";
import { toApiDate } from "@/lib/utils/date";

const RECENT_LOG_LIMIT = 5;
const SWIPE_THRESHOLD = 60;
const SWIPE_MAX = 96;

export function RecentLogs({
  items,
  currentUserId,
  onDelete,
  onEdit,
  editingId,
  pendingDeleteId,
}: {
  items: ProgressLogOut[];
  currentUserId: string | null;
  onDelete: (id: string) => void;
  /** W50.3 D-1 — load an entry back into the sheet form. */
  onEdit: (item: ProgressLogOut) => void;
  /** Which entry is currently loaded into the form, if any. */
  editingId: string | null;
  pendingDeleteId: string | null;
}) {
  const limited = items.slice(0, RECENT_LOG_LIMIT);
  return (
    <section
      data-component="MobileLogSheetLogList"
      data-testid="mobile-log-list"
      className="mx-s-5 border-t border-hairline pt-s-3"
    >
      <header className="flex items-center justify-between mb-s-2">
        <h3 className="text-meta font-semibold text-ink-2">最近紀錄</h3>
        <span className="text-meta text-ink-3">{items.length} 筆</span>
      </header>
      {limited.length === 0 ? (
        <p className="text-meta text-ink-3 py-s-2">（還沒有紀錄）</p>
      ) : (
        <ul className="flex flex-col">
          {limited.map((it) => (
            <SwipeRow
              key={it.id}
              log={it}
              canDelete={Boolean(
                currentUserId && it.logged_by === currentUserId,
              )}
              // W50.3: edit reuses the same gate as delete. Both are author-only
              // in the backend, so offering one without the other would put a
              // control on screen that is guaranteed to 403.
              canEdit={Boolean(currentUserId && it.logged_by === currentUserId)}
              isEditing={editingId === it.id}
              onEdit={() => onEdit(it)}
              isDeleting={pendingDeleteId === it.id}
              onDelete={() => onDelete(it.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SwipeRow({
  log,
  canDelete,
  canEdit,
  isEditing,
  onEdit,
  isDeleting,
  onDelete,
}: {
  log: ProgressLogOut;
  canDelete: boolean;
  canEdit: boolean;
  isEditing: boolean;
  onEdit: () => void;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLLIElement | null>(null);
  const [offset, setOffset] = useState(0);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLLIElement>) => {
    if (!canDelete) return;
    startRef.current = { x: e.clientX, y: e.clientY };
    ref.current?.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLLIElement>) => {
    const s = startRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dx > 0) {
      setOffset(0);
      return;
    }
    const next = Math.max(-SWIPE_MAX, dx);
    setOffset(next);
  };
  const onPointerEnd = () => {
    const s = startRef.current;
    startRef.current = null;
    if (!s) return;
    if (offset <= -SWIPE_THRESHOLD && canDelete) {
      onDelete();
    }
    setOffset(0);
  };

  return (
    <li
      ref={ref}
      data-component="MobileLogSheetRow"
      data-testid={`mobile-log-row-${log.id}`}
      data-log-id={log.id}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      className="relative border-b border-hairline last:border-b-0"
    >
      {canDelete ? (
        <span
          aria-hidden
          className="absolute inset-y-0 right-0 w-24 flex items-center justify-center bg-red-100 text-ink-1 text-meta"
        >
          刪除
        </span>
      ) : null}
      <div
        className="flex items-center gap-3 py-s-2 bg-paper-elevated touch-pan-y"
        style={{
          transform: `translateX(${offset}px)`,
          transition: offset === 0 ? "transform 120ms" : "none",
        }}
      >
        <AuthorAvatar
          tone="you"
          size="xs"
          label={log.logged_by.slice(0, 1)}
        />
        <div className="flex-1 min-w-0">
          <p className="text-body-sm font-semibold text-ink-1 tabular-nums truncate">
            {log.note || "（無備註）"}
          </p>
          <p className="text-meta text-ink-3 tabular-nums">
            {formatTimeAgo(log.logged_at)}
          </p>
        </div>
        <span className="text-body-sm tabular-nums font-semibold text-ink-1">
          +{Math.abs(log.amount_delta).toLocaleString()}
        </span>
        {/*
          W50.3 D-1 — the edit affordance is a **button**, not a tap target on the
          row. Two reasons, both learned here rather than assumed:

          1. This `<li>` already calls `setPointerCapture` on pointerdown for
             swipe-to-delete. ADR-0156 measured that capture can stop the browser
             synthesising `click` when the down/up targets disagree — the exact
             failure that made a sheet's ✕ need four taps. A button inside the
             row never has to win that arbitration.
          2. A whole-row tap would enter edit mode on every stray touch, and edit
             mode repopulates the keypad.

          44pt per the mobile-components skill; `aria-label` names the amount so
          the row's meaning survives without the surrounding context.
        */}
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`編輯 ${Math.abs(log.amount_delta).toLocaleString()} 這筆`}
            aria-pressed={isEditing}
            data-testid={`mobile-log-edit-${log.id}`}
            data-editing={isEditing ? "y" : "n"}
            className="press inline-flex items-center justify-center min-h-11 min-w-11 -my-s-2 rounded-full text-ink-2 aria-pressed:bg-ink-1/10"
          >
            <Pencil size={16} strokeWidth={1.5} aria-hidden />
          </button>
        ) : null}
        {isDeleting ? (
          <span className="text-meta text-ink-3">…</span>
        ) : null}
      </div>
    </li>
  );
}

function formatTimeAgo(iso: string): string {
  // `toApiDate` — the API sends naive UTC; see `lib/utils/date.ts::toApiDate`.
  const then = toApiDate(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "剛剛";
  if (m < 60) return `${m} 分鐘前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小時前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} 天前`;
  return iso.slice(0, 10);
}